import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Compass, Heart, Handshake, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1643104897460-ab6b868eb59b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHx2YW5jb3V2ZXIlMjBza3lsaW5lJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NzA1NTkwNHww&ixlib=rb-4.1.0&q=85";
const STORY_IMG = "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1400&q=80";

const values = [
  { icon: Compass, color: "var(--accent-teal)", title: "Canadian compliance-first", desc: "Provincial labour law, T4/T4A, PIPEDA and Quebec Law 25 — non-negotiable on every placement." },
  { icon: Heart, color: "var(--accent-coral)", title: "Candidate dignity", desc: "We never blast résumés. Every profile is handled personally and with written consent." },
  { icon: Handshake, color: "var(--accent-mint)", title: "Transparent economics", desc: "Bill rate, pay rate, and total-cost-of-hire shared upfront. No surprise margins, ever." },
  { icon: ShieldCheck, color: "var(--accent-amber)", title: "Quality before quantity", desc: "Fewer, better shortlists. We'd rather send three great candidates than twelve mediocre ones." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Ashren"
        title={<>A Canadian firm, built for the way <span className="italic" style={{ color: "var(--accent-purple)" }}>hiring actually happens.</span></>}
        subtitle="Ashren was founded on a simple idea: the best staffing firms in Canada don't chase placements — they solve problems. We specialize in the roles everyone else struggles to fill, and we do it with the compliance depth that Canadian businesses require."
        image={HERO_IMG}
        imageAlt="Vancouver skyline"
        testId="about-hero"
      />

      <section className="py-20 md:py-24 bg-white border-y border-[color:var(--border)]" data-testid="about-story">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl border border-[color:var(--border)]">
              <img src={STORY_IMG} alt="Modern workspace" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="eyebrow mb-3">Our story</div>
            <h2 className="display-lg mb-6">Founded in Vancouver. Delivered across Canada.</h2>
            <div className="space-y-4 text-[color:var(--muted)] leading-relaxed text-lg">
              <p>
                Ashren Consulting Ltd. was founded to bring boutique-specialist hiring standards
                to a market dominated by volume-driven generalist recruiters. We saw Canadian
                scaleups and midsize enterprises lose months — and seven-figure opportunities —
                waiting for vendors who didn&apos;t understand the role, the market, or the compliance.
              </p>
              <p>
                Today, we operate in two specialist lanes. A niche technology practice for roles
                that generic recruiters pass over — AI/ML, cloud, security, quantum. And a
                full-cycle general staffing practice for the finance, legal, healthcare, and
                operational hires that keep Canadian businesses running.
              </p>
              <p>
                We work on a single principle: the clients who trust us with the hardest hire
                usually trust us with the next ten.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="about-values">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow mb-3">What we stand for</div>
            <h2 className="display-lg">Four non-negotiables.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="card h-full" data-testid={`value-card-${i}`}>
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                       style={{ background: `${v.color}22`, color: v.color }}>
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-xl mb-2">{v.title}</h3>
                  <p className="text-sm text-[color:var(--muted)] leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand
        title="Have a role in mind?"
        subtitle="The first conversation is always free. No pitch deck, no pressure — just a clear read on the market."
        primaryLabel="Request a talent brief"
        primaryTo="/for-employers"
        secondaryLabel="Contact us"
        secondaryTo="/contact"
      />
    </div>
  );
}
